import React, { useState, useEffect, useCallback } from 'react';
import { SERVER_URL } from '../../constants';
import '../GroupCard.css';

function StudentGroups(props) {
  const { selectedPerson } = props;
  const [roomGroups, setRoomGroups] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [activityGroups, setActivityGroups] = useState([]);
  const [transportGroups, setTransportGroups] = useState([]);
  const [otherGroups, setOtherGroups] = useState([]);
  const [afternoonBookings, setAfternoonBookings] = useState([]);
  const [classBookings, setClassBookings] = useState([]);
  const [otherBookings, setOtherBookings] = useState([]);
  const[transfers, setTransfers] = useState([]);
  const [todayTransfers, setTodayTransfers] = useState([]);
  

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Use today's date as initial state
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));


  const fetchGroups = useCallback(() => {
    console.log("selectedPerson.id:", selectedPerson?.id, "selectedDate:", selectedDate);

    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/myGroupsToday/${selectedPerson.id}/${selectedDate}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(async response => {
      if (!response.ok) throw new Error('Failed to fetch groups');
      const text = await response.text();
      return text ? JSON.parse(text) : [];
    })
    .then(data => {
      // The live group types written by the API are ROOM, CLASS, ACTIVITY and
      // TRANSPORT. AFTERNOON is a retired type kept here only so historic dates
      // still read; the Activities module has written ACTIVITY since it replaced
      // the old scheduler. Filtering on AFTERNOON alone made every modern
      // activity group invisible on the screen whose job is saying where a
      // student is.
      const ACTIVITY_TYPES = ["ACTIVITY", "AFTERNOON"];
      const KNOWN_TYPES = ["ROOM", "CLASS", "TRANSPORT", ...ACTIVITY_TYPES];

      setRoomGroups(data.filter(group => group.groupType === "ROOM"));
      setClassGroups(data.filter(group => group.groupType === "CLASS"));
      setActivityGroups(data.filter(group => ACTIVITY_TYPES.includes(group.groupType)));
      setTransportGroups(data.filter(group => group.groupType === "TRANSPORT"));
      // Anything untyped or of a type this screen doesn't know about. Previously
      // these were lumped in with rooms, which asserted a bedroom that may not
      // exist. Showing them plainly is worse-looking and more honest than either
      // mislabelling them or dropping them silently.
      setOtherGroups(data.filter(group => !KNOWN_TYPES.includes(group.groupType)));
    })
    .catch(err => console.error(err));
  }, [selectedPerson.id, selectedDate]);

  const fetchBookings = useCallback(() => {
    console.log("selectedPerson.id:", selectedPerson?.id, "selectedDate:", selectedDate);

    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/whatamidoing/${selectedPerson.id}/${selectedDate}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(async response => {
      if (!response.ok) throw new Error('Failed to fetch groups');
      const text = await response.text();
      return text ? JSON.parse(text) : [];
    })
    .then(data => {
      setOtherBookings(data.filter(group => group.groupType === "RESIDENTIAL" || group.groupType === "EXTRA"  || group.groupType === "UNKNOWN"));
      setClassBookings(data.filter(group => group.groupType === "RESIDENTIAL"));
      setAfternoonBookings(data.filter(group => group.groupType === "AFTERNOON" || group.groupType === "WEEKEND" || group.groupType === "EXTERNAL"));
    })
    .catch(err => console.error(err));
  }, [selectedPerson.id, selectedDate]);
  
  const fetchTransfers = useCallback(() => {
    console.log("selectedPerson.id:", selectedPerson?.id, "selectedDate:", selectedDate);

    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/transfers/${selectedPerson.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(async response => {
      if (!response.ok) throw new Error('Failed to fetch transfers');
      const text = await response.text();
      return text ? JSON.parse(text) : [];
    })
    .then(data => {
      setTransfers(data);
      const todayTransfers = data.filter(transfer => formatDate(new Date(transfer.transferDate)) === selectedDate);
      setTodayTransfers(todayTransfers);
    })
    .catch(err => console.error(err));
  }, [selectedPerson.id, selectedDate]);

  const shiftDate = (deltaDays) => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const newDate = new Date(year, month - 1, day); // JS months are 0-based
    newDate.setDate(newDate.getDate() + deltaDays);
    setSelectedDate(formatDate(newDate));
  };

  useEffect(() => {
    fetchGroups();
    fetchTransfers();
    fetchBookings();
  }, [fetchGroups, fetchTransfers, fetchBookings]);

  return (
    <div className="detail-card payments-card" style={{ padding: '20px' }}>
      <div style={{ marginBottom: '15px' }}>
        <button type="button" onClick={() => shiftDate(-1)}>←</button>
        <span style={{ margin: '0 10px' }}>{selectedDate}</span>
        <button type="button" onClick={() => shiftDate(1)}>→</button>
      </div>
      <section>
        <h3>Room:</h3>
        {roomGroups.length > 0 ? roomGroups.map(group => <p key={group.groupId}>{group.groupName}</p>) : <p>Not Allocated yet</p>}
      </section>
      {transportGroups.length > 0 && (
        <section>
          <h3>Transport:</h3>
          {transportGroups.map(group =>
            <p key={group.groupId}>{group.groupName}{group.adultName ? ` - Leader: ${group.adultName} ${group.adultSurname}` : ""}</p>
          )}
        </section>
      )}
      {todayTransfers.length > 0 ? (
        <section>
          <h3>Flight Today:</h3>
          {todayTransfers.map(transfer => (
            <div key={transfer.id}>
              <p>Depart: {transfer.depart} at {transfer.departureTime}</p>
              <p>Arrive: {transfer.arrive} at {transfer.arrivalTime}</p>
              <p>Flight No: {transfer.flightId}</p>
            </div>  
          ))}
        </section>
      ) : (
        <>
          <section>
            <h3>Class:</h3>
            {classGroups.length > 0 ? classGroups.map(group =>
              <p key={group.groupId}>{group.groupName} - Teacher: {group.adultName} {group.adultSurname}</p>
            ) : <p>Not Allocated yet</p>}
          </section>
          <section>
            <h3>Afternoon:</h3>
            {activityGroups.length > 0 ? (
              activityGroups.map(group =>
                <p key={group.groupId}>{group.groupName}{group.adultName ? ` - Leader: ${group.adultName} ${group.adultSurname}` : ""}</p>
              )
            ) : (
              afternoonBookings.length > 0 ? (
                // Fallback only. These are products the student has BOUGHT, not
                // where they are — "Excursion Package" rather than "Kayaking".
                // Labelled as such so nobody reads it as a location.
                afternoonBookings.map(booking =>
                  <p key={booking.id}>
                    Booked: {booking.groupName} 
                    {booking.groupType === "EXTERNAL" ? ` - ${booking.groupType}` : ""}
                    <span style={{ color: '#999', fontSize: '11px' }}> (not yet assigned to a group)</span>
                  </p>
                )
              ) : <p>NONE</p>
              
            )}
          </section>
        </>
      )}
      {otherGroups.length > 0 && (
        <section>
          <h3>Other:</h3>
          {otherGroups.map(group =>
            <p key={group.groupId}>{group.groupName}{group.groupType ? ` (${group.groupType})` : " (untyped)"}</p>
          )}
        </section>
      )}
      <p style={{ color: '#999', fontSize: '10px' }}>Student: {selectedPerson.id}</p>
      <p style={{ color: '#999', fontSize: '10px' }}>Is authenticated: {sessionStorage.getItem('isAuthenticated')?.toString()}</p>
    </div>
  );

}

export default StudentGroups;
